import { AU, DT, EarthRadiusKM, intervals, kAU, } from "../astronomy/AstroConstants.js";
import { Rad2Deg, circleRemainder, longitude } from "../math/MathEx.js";
import { JDtoTDB } from "../astronomy/AstroDate.js";
import { Vector3 } from "../math/Vector3.js";
import { Quaternion } from "../math/Quaternion.js";
import { RefractionForwardCorrection } from "../astronomy/Refraction.js";
import { altitude, azimuth } from "../math/SkyUtils.js";
export class SolarSystemObject {
    id;
    name;
    radius;
    parent;
    orbit;
    meridianTimeCache;
    culminationCache;
    moonRiseSetTimesCache;
    constructor(id, name, radius, orbitCreator, parent) {
        this.id = id;
        this.orbit = orbitCreator(parent?.orbit);
        this.radius = radius * kAU;
        this.name = name;
        this.parent = parent;
        this.meridianTimeCache = new Map();
        this.culminationCache = new Map();
        this.moonRiseSetTimesCache = new Map();
    }
    getAzimuth(jd, navigator) {
        return circleRemainder(azimuth(this.getPosition(jd, navigator).subtract(navigator.position(jd)), // direction from observer
        navigator.lookRotation(jd)), 360);
    }
    getAltitude(JD, navigator) {
        return altitude(this.getPosition(JD, navigator).subtract(navigator.position(JD)), // direction from observer
        navigator.up(JD));
    }
    getCorrectedAltitude(JD, navigator) {
        return (this.getAltitude(JD, navigator) +
            this.getRiseSetCorrection(JD, navigator));
    }
    meridianTime(jd, navigator) {
        const observerLongitude = longitude(navigator.up(jd), navigator.planetLocalRotation(jd));
        const value = this.calculateTime(navigator, (jd, navigator) => Math.abs(longitude(this.getPosition(jd, navigator), navigator.planetLocalRotation(jd)) - observerLongitude), jd, jd + 1, -1);
        if (Math.abs(value - jd) < 1e-5 || Math.abs(value - (jd + 1)) < 1e-5)
            return null;
        return value;
    }
    getMeridianTime(jd, n) {
        const cacheKey = `${n.id}:${jd}`;
        const cached = this.meridianTimeCache.get(cacheKey);
        if (cached)
            return cached;
        const value = this.meridianTime(jd, n);
        this.meridianTimeCache.set(cacheKey, value);
        return value;
    }
    culminationTime(jd, n) {
        return this.calculateTime(n, (jd, n) => this.getCorrectedAltitude(jd, n), jd, jd + 1);
    }
    getCulmination(jd, n) {
        const cacheKey = `${n.id}:${jd}`;
        const cached = this.culminationCache.get(cacheKey);
        if (cached)
            return cached;
        const culminationJd = this.culminationTime(jd, n);
        const value = {
            jd: culminationJd,
            alt: this.getAltitude(culminationJd, n),
            azm: this.getAzimuth(culminationJd, n),
        };
        this.culminationCache.set(cacheKey, value);
        return value;
    }
    calculateTime(navigator, calc, fromJD, toJD, sign = 1) {
        const T = Math.abs(toJD - fromJD);
        const Min = Math.min(fromJD, toJD);
        let rec = -Infinity;
        let a = 0, b = T, arg = (a + b) / 2;
        for (let detailing = 0; detailing < intervals.length; detailing++) {
            const step = intervals[detailing], b1 = b + step;
            for (let jd_Min = a; jd_Min < b1; jd_Min += step) {
                const tmp_Min = Math.min(jd_Min, b);
                const curRise = calc(Min + tmp_Min, navigator) * sign;
                if (rec < curRise) {
                    rec = curRise;
                    arg = tmp_Min;
                }
            }
            if (detailing < intervals.length - 1) {
                const r = step - intervals[detailing + 1];
                a = Math.max(a, arg - r);
                b = Math.min(b, arg + r);
            }
        }
        return Min + arg;
    }
    getMoonRiseSetTimes(jd, n) {
        if (this.id !== 31) {
            throw new Error("Method available only for Moon.");
        }
        const cacheKey = `${n.id}:${jd}`;
        const cached = this.moonRiseSetTimesCache.get(cacheKey);
        if (cached)
            return cached;
        let _jd = jd;
        const max = jd + 1;
        const result = [];
        while (_jd < max) {
            const zeroJd = this.getNextCrossZeroTime(n, jd, max);
            if (zeroJd === null)
                break;
            result.push({
                jd: zeroJd,
                kind: this.getCorrectedAltitude(zeroJd + DT, n) > 0
                    ? "rise"
                    : "set",
                azm: this.getAzimuth(zeroJd, n),
            });
            jd = zeroJd + DT;
        }
        this.moonRiseSetTimesCache.set(cacheKey, result);
        return result;
    }
    getNextCrossZeroTime(navigator, fromJD, toJD) {
        const crossJD = this.nextCrossZeroTime(navigator, (jd, n) => {
            return this.getCorrectedAltitude(jd, n);
        }, fromJD, toJD);
        return crossJD > Math.max(fromJD, toJD) ||
            crossJD < Math.min(fromJD, toJD)
            ? null
            : crossJD;
    }
    nextCrossZeroTime(navigator, altitude, fromJD, toJD) {
        const T = Math.abs(toJD - fromJD);
        const timeDirection = Math.sign(toJD - fromJD);
        let cur = 0;
        const Step = 1.0 / 50; //half of minimal period
        let curY = altitude(fromJD + timeDirection * cur, navigator);
        let curY_ = altitude(fromJD + timeDirection * (cur + DT), navigator);
        if (Math.sign(curY) != Math.sign(curY_))
            return fromJD + timeDirection * (cur + DT / 2);
        let curDir = Math.sign(curY_ - curY);
        //find next monotone interval
        for (let next = cur + Step; next < T + Step && cur < T; next += Step) {
            const nextY = altitude(fromJD + timeDirection * next, navigator);
            const nextY_ = altitude(fromJD + timeDirection * (next + DT), navigator);
            const nextDir = Math.sign(nextY_ - nextY);
            if (nextDir == curDir && next < T)
                continue;
            //find peak
            let left = next - Step, right = next;
            while (right - left > DT) {
                const middle = (left + right) / 2;
                const y = altitude(fromJD + timeDirection * middle, navigator);
                const y_ = altitude(fromJD + timeDirection * (middle + DT), navigator);
                const dir = Math.sign(y_ - y);
                if (dir == nextDir)
                    right = middle;
                else
                    left = middle;
            }
            const peak = Math.min((left + right) / 2, T);
            const peakY = altitude(fromJD + timeDirection * peak, navigator);
            if (Math.sign(peakY) == Math.sign(curY) &&
                Math.sign(peakY) == Math.sign(nextY)) {
                //peak doesn't cross horizon
                cur = next;
                curY = nextY;
                curY_ = nextY_;
                curDir = Math.sign(curY_ - curY);
                continue;
            }
            //find zero
            // let leftSign;
            let rightSign;
            if (Math.sign(peakY) != Math.sign(curY)) {
                left = cur + DT;
                right = peak;
                // leftSign = Math.sign(curY);
                rightSign = Math.sign(peakY);
            }
            else {
                left = peak;
                right = next;
                // leftSign = Math.sign(peakY);
                rightSign = Math.sign(nextY);
            }
            while (right - left > DT) {
                const middle = (left + right) / 2;
                const y = altitude(fromJD + timeDirection * middle, navigator);
                if (Math.sign(y) == rightSign)
                    right = middle;
                else
                    left = middle;
            }
            return fromJD + timeDirection * ((left + right) / 2);
        }
        return 0;
    }
    angularSize(JD, navigator) {
        const distance = Vector3.Distance(this.getPosition(JD, navigator), navigator.position(JD));
        return Math.atan2(this.radius, distance) * 2;
    }
    getRiseSetCorrection(JD, navigator) {
        const angularSize = (this.angularSize(JD, navigator) / 2) * Rad2Deg;
        const correction = this.getRefractionCorrection(JD, navigator);
        return angularSize + correction;
    }
    getRefractionCorrection(JD, navigator) {
        const altitude = this.getAltitude(JD, navigator);
        const angularSize = (this.angularSize(JD, navigator) / 2) * Rad2Deg;
        const correction = RefractionForwardCorrection(altitude + angularSize);
        return correction;
    }
    CosChi(sun, jd, navigator) {
        const sunPosition = sun.getPosition(jd, navigator);
        const d = 1 / kAU;
        const observerPosition = navigator
            .position(jd)
            .subtract(sunPosition)
            .multiplyByFloat(d);
        const observerRq = Vector3.SqrMagnitude(observerPosition);
        const planetPosition = this.getPosition(jd, navigator)
            .subtract(sunPosition)
            .multiplyByFloat(d);
        const planetRq = Vector3.SqrMagnitude(planetPosition);
        const observerPlanetRq = Vector3.SqrMagnitude(observerPosition.subtract(planetPosition));
        return ((observerPlanetRq + planetRq - observerRq) /
            (2 * Math.sqrt(observerPlanetRq * planetRq)));
    }
    illumination(sun, jd, navigator) {
        if (this.id == 0)
            return 0;
        const cosChi = this.CosChi(sun, jd, navigator);
        return 0.5 * Math.abs(1 + cosChi);
    }
    unsignedPhaseAngle(jd, navigator) {
        const pos = this.getPosition(jd, navigator);
        const realPos = this._getPosition(jd, Vector3.Zero());
        return Vector3.Angle(realPos.multiplyByFloat(-1), pos.multiplyByFloat(-1));
    }
    RotationEquatorialToVSOP(jd) {
        let rotation = this.localRotation(jd);
        if (this.parent) {
            rotation = this.parent
                .RotationEquatorialToVSOP(jd)
                .multiply(rotation);
        }
        return rotation;
    }
    CalculationTime(jd) {
        return JDtoTDB(jd);
    }
    TrajectoryMaxPeriod = 10000000;
    localRotation(jd) {
        const time = this.CalculationTime(jd);
        const solarOrbit = this.orbit;
        if (null == solarOrbit)
            return Quaternion.Identity();
        if (solarOrbit.HasRotation()) {
            const equator = solarOrbit.GetEquatorOrientation(time);
            const spin = solarOrbit.GetSpin(time);
            return Quaternion.Rh2Lh2(Quaternion.Inverse(spin.multiply(equator)));
        }
        const curPos = solarOrbit.getPosition(time);
        const next = solarOrbit.getPosition(time +
            Math.min(this.TrajectoryMaxPeriod, this.orbit.OrbitPeriod()) /
                1000);
        const forward = next.subtract(curPos).normalize();
        const normal = curPos.normalize();
        const realForward = forward
            .subtract(Vector3.MultiplyByNum(normal, Vector3.Dot(forward, normal)))
            .normalize();
        const lookAt = Quaternion.LookRotation(realForward, curPos.normalize());
        return lookAt;
    }
    _getPosition(jd, zeroPoint) {
        const time = this.CalculationTime(jd);
        return this.HeliocentricEclipticPosition(time).subtract(zeroPoint);
    }
    getPosition(jd, navigator) {
        return this._getPosition(jd, navigator.center(jd));
    }
    HeliocentricEclipticPosition(jd) {
        let eclipticPosition = this.EclipticPosition(jd);
        if (this.parent) {
            eclipticPosition = eclipticPosition.add(this.HeliocentricEclipticParentPosition(this, jd));
        }
        return eclipticPosition;
    }
    HeliocentricEclipticParentPosition(orbit, jd) {
        if (!orbit.parent)
            return Vector3.Zero();
        return orbit.parent.HeliocentricEclipticPosition(jd);
    }
    EclipticPosition(jd) {
        return this.orbit.getPosition(jd);
    }
    apparentMagnitude(sun, jd, navigator) {
        if (this.id !== 31) {
            throw new Error("Method available only for Moon.");
        }
        const phaseRad = this.unsignedPhaseAngle(jd, navigator);
        const sunPosition = sun._getPosition(jd, Vector3.Zero());
        const distanceBodyObserver = this.distanceBodyObserver(jd, navigator);
        const distanceBodySun = this.distanceBodySun(sun, jd);
        const realPos = this._getPosition(jd, Vector3.Zero());
        const phaseCos = Math.cos(phaseRad);
        const parentPosition = navigator.center(jd);
        const distanceParentSun = Vector3.Distance(parentPosition, sunPosition) / AU;
        const distanceBodyObserver2 = distanceBodyObserver * distanceBodyObserver;
        const distanceBodySun2 = distanceBodySun * distanceBodySun;
        const distanceParentSun2 = distanceParentSun * distanceParentSun;
        const posTimeParentPos = Vector3.Dot(realPos, parentPosition);
        let shadowFactor = 1.0;
        const earthRadius = EarthRadiusKM;
        const radius = this.radius;
        if (posTimeParentPos > distanceParentSun2) {
            const sunRadius = (109.0 * earthRadius) / AU;
            const sunMinusParentRadius = sunRadius - earthRadius / AU;
            const quot = posTimeParentPos / distanceParentSun2;
            let dd = sunRadius -
                sunMinusParentRadius * quot -
                Math.sqrt((1.0 - sunMinusParentRadius / distanceParentSun) *
                    (distanceBodySun2 - posTimeParentPos * quot));
            if (dd >= radius) {
                shadowFactor = 1e-9;
            }
            else if (dd > -radius) {
                dd /= radius;
                shadowFactor =
                    0.5 -
                        (Math.asin(dd) + dd * Math.sqrt(1.0 - dd * dd)) / Math.PI;
            }
        }
        const phaseSin = Math.sqrt(1.0 - phaseCos * phaseCos);
        const p = (1.0 - phaseRad / Math.PI) * phaseCos + phaseSin / Math.PI;
        const auRadius = radius / AU;
        const albedo = 0.136;
        const F = ((2.0 * albedo * auRadius * auRadius * p) /
            (3.0 * distanceBodyObserver2 * distanceBodySun2)) *
            shadowFactor;
        return -26.73 - 2.5 * Math.log10(F);
    }
    distanceBodyObserver(jd, navigator) {
        const observerPosition = navigator
            .position(jd)
            .add(navigator.center(jd));
        const realPos = this._getPosition(jd, Vector3.Zero());
        return Vector3.Distance(realPos, observerPosition) / AU;
    }
    distanceBodySun(sun, jd) {
        const sunPosition = sun._getPosition(jd, Vector3.Zero());
        const realPos = this._getPosition(jd, Vector3.Zero());
        return Vector3.Distance(realPos, sunPosition) / AU;
    }
    angle(jd, navigator) {
        if (this.id !== 31) {
            throw new Error("Method available only for Moon.");
        }
        const localRotation = this.localRotation(jd);
        const localUp = Vector3.MultiplyQuaternionByVector3(Quaternion.Inverse(localRotation), Vector3.Up());
        const position = this.getPosition(jd, navigator);
        const direction = position.normalize();
        const projectionUp = localUp.subtract(direction.multiplyByFloat(Vector3.Dot(direction, localUp)));
        const navigatorUp = navigator.up(jd);
        const projectionNavigatorUp = navigatorUp.subtract(direction.multiplyByFloat(Vector3.Dot(direction, navigatorUp)));
        return Vector3.SignedAngle(projectionUp, projectionNavigatorUp, direction);
    }
}
